import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Typography, FormControlLabel, Checkbox, Alert } from '@mui/material';
import { useEditUserSecurityMutation } from 'renderer/features/users-page/services/users.endpoints';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUser } from 'renderer/features/auth/state/userSelectors';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import { useFormik } from 'formik';
import { z } from 'zod';
import { toValidator } from 'renderer/shared/schemas/common.schemas';
import { Link } from 'react-router-dom';

const SecuritySchema = z
  .object({
    currentPassword: z.string(),
    newPassword: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters long' }),
    confirmPassword: z.string(),
    enableTwoFactorAuth: z.boolean(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type Security = z.infer<typeof SecuritySchema>;

interface SecurityTabProps {
  userId: string;
}

const SecurityTab: React.FC<SecurityTabProps> = ({ userId }) => {
  const [editUserSecurity, { isLoading, isSuccess, error }] =
    useEditUserSecurityMutation();

  // const user = useAppSelector(selectUser);
  // const userId = user.role === 'public' ? '' : user._id;

  const formik = useFormik<Security>({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      enableTwoFactorAuth: false,
    },
    validate: toValidator(SecuritySchema),
    onSubmit: (values) => {
      editUserSecurity({ ...values, userId });
    },
  });

  return (
    <Box p={2}>
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h6">Change Password</Typography>
        <Typography variant="body2">
          <Link to="/forgot-password">Forgot Password?</Link>
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          label="Enter Current Password"
          type="password"
          name="currentPassword"
          id="currentPassword"
          disabled={isLoading}
          value={formik.values.currentPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.currentPassword && !!formik.errors.currentPassword
          }
          helperText={
            formik.touched.currentPassword && formik.errors.currentPassword
          }
        />
        <TextField
          fullWidth
          margin="normal"
          label="New Password"
          type="password"
          name="newPassword"
          id="newPassword"
          disabled={isLoading}
          value={formik.values.newPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.newPassword && !!formik.errors.newPassword}
          helperText={formik.touched.newPassword && formik.errors.newPassword}
        />
        <TextField
          fullWidth
          margin="normal"
          type="password"
          label="Confirm New Password"
          name="confirmPassword"
          id="confirmPassword"
          disabled={isLoading}
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword && !!formik.errors.confirmPassword
          }
          helperText={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              name="enableTwoFactorAuth"
              id="enableTwoFactorAuth"
              checked={formik.values.enableTwoFactorAuth}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          }
          label="Enable Two-Factor Authentication"
        />
        <br />
        {error && (
          <Alert severity="error">
            <Typography>{formatQueryError(error)}</Typography>
          </Alert>
        )}
        {isSuccess && (
          <Alert severity="success">
            <Typography>Password successfully changed!</Typography>
          </Alert>
        )}
        <Button
          disabled={isLoading || !formik.isValid}
          variant="contained"
          type="submit"
          sx={{ mt: 2 }}
        >
          Update Password
        </Button>
      </form>
    </Box>
  );
};

export default SecurityTab;
